import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ImageBackground,
  Platform,
  Alert,
  ActivityIndicator,
} from "react-native";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  setDoc,
} from "firebase/firestore";
import VenueCard from "./VenueCard";
import { useAdminThemeContext } from "../../contexts/AdminThemeContext";
import { useVenues } from "../VenueContext";
import { setWebPlaceholderColor, getAdminSearchPlaceholderColor } from "../../utils/setWebPlaceholderColor";

const ALL_VENUES_SEARCH_ID = "admin-all-venues-search";

const VenuesList = ({ handlePhotoUpload, handleDeletePhotos }) => {
  const { styles: adminStyles, colors, isDarkMode } = useAdminThemeContext();
    const [venueSortBy, setVenueSortBy] = useState("name"); // options: 'name', 'upvotes', 'downvotes', 'ratio'
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [venueSearchQuery, setVenueSearchQuery] = useState("");
    const [venueFilter, setVenueFilter] = useState("all");
    const [venueLimit, setVenueLimit] = useState(60);
    const [currentPage, setCurrentPage] = useState(1);
    const [editFormData, setEditFormData] = useState({});
    const [editingVenue, setEditingVenue] = useState(null);
    // Bumped on every open / cancel / save so a slow venue reload can't reopen or refill the form.
    const editRequestRef = useRef(0);
    const [isLoading, setIsLoading] = useState(false);
    const { refreshVenues, venues, archivedVenues } = useVenues();
    const db = getFirestore();

    const searchPlaceholderColor = getAdminSearchPlaceholderColor(isDarkMode);

    useEffect(() => {
      setWebPlaceholderColor(ALL_VENUES_SEARCH_ID, searchPlaceholderColor);
    }, [searchPlaceholderColor]);

    const showConfirmation = (title, message, onConfirm) => {
      if (Platform.OS === 'web') {
        if (window.confirm(message)) {
          onConfirm();
        }
      } else {
        Alert.alert(
          title,
          message,
          [
            {
              text: "Cancel",
              style: "cancel"
            },
            {
              text: "OK",
              onPress: onConfirm
            }
          ]
        );
      }
    };

  const pillStyle = (active, danger = false) => ({
    backgroundColor: active
      ? '#0bb5df'
      : danger
        ? (isDarkMode ? 'rgba(231, 76, 60, 0.2)' : 'rgba(231, 76, 60, 0.12)')
        : colors.cardBgSubtle,
    borderWidth: active ? 0 : 1,
    borderColor: danger
      ? (isDarkMode ? 'rgba(231, 76, 60, 0.4)' : 'rgba(231, 76, 60, 0.25)')
      : colors.cardBorder,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    minWidth: 120,
    alignItems: 'center',
  });

  const pillTextStyle = (active) => ({
    color: active ? '#ffffff' : colors.textPrimary,
    fontFamily: 'VastagoGrotesk',
    fontSize: 14,
    fontWeight: active ? '600' : '500',
  });

  const navBtnStyle = (active = false, disabled = false) => ({
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: active ? '#0bb5df' : colors.cardBgSubtle,
    borderRadius: 8,
    borderWidth: active ? 0 : 1,
    borderColor: colors.cardBorder,
    opacity: disabled ? 0.5 : 1,
  });

  const handleExportCSV = () => {
    // Add headers
    const headers = [
      'STATE/COUNTRY',
      'VENUE',
      'CITY',
      'WEBSITE',
      'CAPACITY',
      'AFFILIATE',
      'STAGE',
      'SPEC SHEET LINK',
      'POWER',
      'LIGHTING CONTROL',
      'LIGHTING FIXTURES',
      'VIDEO',
      'FOH',
      'PA',
      'MONITORS',
      'BACKSTAGE',
      'BACKLINE',
      'MERCH',
      'PARKING',
      'SHORE POWER',
      'NOTES',
      'MAP Embed Link',
      'UPDATED AT',
      'ARTIST GRADE',
      'EMPLOYEE GRADE',
      'CREW GRADE',
      'OTHER GRADE',
      'OVERALL GRADE',
      'NUMBER OF REPORTS'
    ];

    // Helper function to escape CSV field
    const escapeField = (field) => {
      if (field === null || field === undefined) return '';
      const stringField = String(field);
      
      // Trim BOM characters but preserve internal spaces and line breaks
      const cleanField = stringField.replace(/^\uFEFF/, '');
      
      // If field contains quotes, commas, or newlines, wrap in quotes and escape existing quotes
      if (cleanField.includes('"') || cleanField.includes(',') || cleanField.includes('\n') || cleanField.includes('\r')) {
        // Double the quotes but preserve line breaks
        return `"${cleanField.replace(/"/g, '""')}"`;
      }
      return cleanField;
    };

    // Create CSV content with properly escaped fields
    const csvRows = [
      headers.join(','),
      ...venues.map(venue => [
        escapeField(venue.state),
        escapeField(venue.name),
        escapeField(venue.city),
        escapeField(venue.website),
        escapeField(venue.capacity),
        escapeField(venue.affiliate),
        escapeField(venue.stage),
        escapeField(venue.specSheetLink),
        escapeField(venue.power),
        escapeField(venue.lightingControl),
        escapeField(venue.lightingFixtures),
        escapeField(venue.video),
        escapeField(venue.foh),
        escapeField(venue.pa),
        escapeField(venue.monitors),
        escapeField(venue.backstage),
        escapeField(venue.backline),
        escapeField(venue.merch),
        escapeField(venue.parking),
        escapeField(venue.shorePower),
        escapeField(venue.notes),
        escapeField(venue.mapEmbedLink),
        escapeField(venue.updatedAt ? new Date(venue.updatedAt.seconds * 1000).toISOString() : ''),
        escapeField(venue.approved?.artistGrade),
        escapeField(venue.approved?.employeeGrade),
        escapeField(venue.approved?.crewGrade),
        escapeField(venue.approved?.otherGrade),
        escapeField(venue.approved?.overallGrade),
        escapeField(venue.approved?.reportCount)
      ].join(','))
    ];

    // Create and download the file
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `venues_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Define a handleVenueClick function to navigate to the venue detail


  const handleArchiveVenue = async (venue) => {
    showConfirmation(
      'Archive Venue',
      'Are you sure you want to archive this venue? It will be moved to archived venues.',
      async () => {
        try {
          setIsLoading(true);
          // Create a new document in archived_venues collection
          const archivedVenueRef = doc(db, "archived_venues", venue.id);
          
          // Add archive metadata to the venue data
          const archivedVenue = {
            ...venue,
            archivedAt: new Date(),
          };
          
          // Save to archived_venues collection
          await setDoc(archivedVenueRef, archivedVenue);
          
          // Delete from active venues collection
          const venueRef = doc(db, "venues", venue.id);
          await deleteDoc(venueRef);
          
          await refreshVenues();
        } catch (error) {
          console.error("Error archiving venue:", error);
          alert("Error archiving venue: " + error.message);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  const handleRestoreVenue = async (venue) => {
    showConfirmation(
      'Restore Venue',
      'Are you sure you want to restore this venue? It will be moved back to active venues.',
      async () => {
        try {
          setIsLoading(true);
          // Create a new document in venues collection
          const venueRef = doc(db, "venues", venue.id);
          
          // Remove archive metadata from the venue data
          const { archived, archivedAt, ...restoredVenue } = venue;
          
          // Save to venues collection
          await setDoc(venueRef, restoredVenue);
          
          // Delete from archived_venues collection
          const archivedVenueRef = doc(db, "archived_venues", venue.id);
          await deleteDoc(archivedVenueRef);
          
          await refreshVenues();
        } catch (error) {
          console.error("Error restoring venue:", error);
          alert("Error restoring venue: " + error.message);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  const handleDeleteVenue = async (venue) => {
    showConfirmation(
      'Delete Venue',
      'Are you absolutely sure you want to delete this venue? This action CANNOT be undone!',
      async () => {
        try {
          setIsLoading(true);
          const venueRef = doc(db, "venues", venue.id);
          await deleteDoc(venueRef);
          await refreshVenues();
          // If the deleted venue was selected, clear the selection
          if (selectedVenue?.id === venue.id) {
            setSelectedVenue(null);
          }
        } catch (error) {
          console.error("Error deleting venue:", error);
          alert("Error deleting venue: " + error.message);
        } finally {
          setIsLoading(false);
        }
      }
    );
  };

  const handleVenueClick = (venue) => {
    setSelectedVenue(venue);
  };

  const handleCancelEdit = () => {
    editRequestRef.current += 1;
    setEditingVenue(null);
    setEditFormData({});
  };

  const handleEditField = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveVenue = async () => {
    try {
      if (!editingVenue) return;

      const db = getFirestore();
      const venueRef = doc(db, editingVenue.archived ? "archived_venues" : "venues", editingVenue.id);

      // Update the document with edited fields
      await updateDoc(venueRef, {
        ...editFormData,
        updatedAt: new Date(),
      });

      // Refresh the venue list
      refreshVenues();
      // Update selectedVenue with the edited data if it's currently selected
      if (selectedVenue?.id === editingVenue.id) {
        setSelectedVenue({
          ...selectedVenue,
          ...editFormData
        });
      }

      // Reset editing state
      editRequestRef.current += 1;
      setEditingVenue(null);
      setEditFormData({});

      alert("Venue updated successfully!");
    } catch (error) {
      console.error("Error updating venue:", error);
      alert("Error updating venue: " + error.message);
    }
  };


  // Seed the edit form from the venue doc as it is now, not the list loaded with the page:
  // specs promoted from a LIVE report (or edited elsewhere) after load must show up (BUG-089).
  const startEditing = async (venue) => {
    const request = ++editRequestRef.current;
    let fresh = venue;
    try {
      const snap = await getDoc(doc(getFirestore(), venue.archived ? "archived_venues" : "venues", venue.id));
      if (snap.exists()) fresh = { ...venue, ...snap.data(), id: venue.id };
    } catch (error) {
      console.warn("Could not reload venue before editing; using the list copy:", error);
    }
    if (request !== editRequestRef.current) return;
    setEditingVenue(fresh);
    setEditFormData({
      name: fresh.name || "",
      address: fresh.address || "",
      city: fresh.city || "",
      state: fresh.state || "",
      capacity: fresh.capacity || "",
      website: fresh.website || "",
      stage: fresh.stage || "",
      power: fresh.power || "",
      adaAccessibilityArtist: fresh.adaAccessibilityArtist || "",
      lightingControl: fresh.lightingControl || "",
      lightingFixtures: fresh.lightingFixtures || "",
      video: fresh.video || "",
      foh: fresh.foh || "",
      pa: fresh.pa || "",
      monitors: fresh.monitors || "",
      affiliate: fresh.affiliate || "",
      backline: fresh.backline || "",
      backstage: fresh.backstage || "",
      merch: fresh.merch || "",
      parking: fresh.parking || "",
      shorePower: fresh.shorePower || "",
      specSheetLink: fresh.specSheetLink || "",
      mapEmbedLink: fresh.mapEmbedLink || "",
      notes: fresh.notes || "",
      manualsLinks: fresh.manualsLinks || [],
      specSheets: fresh.specSheets || [],
    });
  };

  const getVenueBackgroundImage = (venue) => {
    if (venue.backgroundImage) {
      return venue.backgroundImage;
    }

    if (venue.photos && venue.photos.length > 0) {
      return venue.photos[0].url || venue.photos[0];
    }

    if (venue.venue_media && typeof venue.venue_media === "string") {
      try {
        const mediaData = JSON.parse(venue.venue_media);
        if (mediaData.photos && mediaData.photos.length > 0) {
          return mediaData.photos[0];
        }
      } catch (error) {
        console.error("Error parsing venue media:", error);
      }
    }

    return null;
  };

  const getFilteredVenues = () => {
    const filteredBySearch = [...venues, ...archivedVenues.map(venue => ({ ...venue, archived: true }))].filter((venue) => {
      const searchLower = venueSearchQuery.toLowerCase();
      return (
        venue.name.toLowerCase().includes(searchLower) ||
        (venue.city && venue.city.toLowerCase().includes(searchLower)) ||
        (venue.state && venue.state.toLowerCase().includes(searchLower))
      );
    });

    // Filter based on the selected filter
    const filtered = filteredBySearch.filter((venue) => {
      const hasPhotos =
        venue.backgroundImage ||
        (venue.photos && venue.photos.length > 0) ||
        (venue.venue_media &&
          typeof venue.venue_media === "string" &&
          JSON.parse(venue.venue_media).photos?.length > 0);

      switch (venueFilter) {
        case "with_photos":
          return hasPhotos && !venue.archived;
        case "without_photos":
          return !hasPhotos && !venue.archived;
        case "not_found":
          return venue.notFoundOnLiveNation && !venue.archived;
        case "archived":
          return venue.archived;
        case "all":
        default:
          return !venue.archived;
      }
    });

    // Sort the venues based on the selected sort option
    const sorted = [...filtered].sort((a, b) => {
      const getTotalUpvotes = (venue) => {
        return (venue.upVotes || 0) + 
               (venue.titleUpVotes || 0) + 
               (venue.specsUpVotes || 0) + 
               (venue.linksUpVotes || 0);
      };
      
      const getTotalDownvotes = (venue) => {
        return (venue.downVotes || 0) + 
               (venue.titleDownVotes || 0) + 
               (venue.specsDownVotes || 0) + 
               (venue.linksDownVotes || 0);
      };
      
      const getOverallRatio = (venue) => {
        const totalUp = getTotalUpvotes(venue);
        const totalDown = getTotalDownvotes(venue);
        return totalUp / (totalUp + totalDown) * 100 || 0;
      };

      switch (venueSortBy) {
        case 'upvotes':
          return getTotalUpvotes(b) - getTotalUpvotes(a);
        case 'downvotes':
          return getTotalDownvotes(b) - getTotalDownvotes(a);
        case 'ratio':
          return getOverallRatio(b) - getOverallRatio(a);
        case 'name':
        default:
          return a.name.localeCompare(b.name);
      }
    });

    // Paginate the results
    const startIndex = (currentPage - 1) * venueLimit;
    return sorted.slice(0, startIndex + venueLimit);
  };

  const handleLoadMore = () => {
    setVenueLimit((prevLimit) => prevLimit + 60);
  };

  const renderPaginationControls = (totalVenues) => {
    const totalPages = Math.ceil(totalVenues / venueLimit);
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
      const pageNumbers = [];
      const showEllipsis = totalPages > 7;

      if (showEllipsis) {
        // Always show first and last page
        pageNumbers.push(1);

        // Show pages around current page
        if (currentPage > 3) {
          pageNumbers.push("...");
        }

        // Pages before current
        if (currentPage > 2) {
          pageNumbers.push(currentPage - 1);
        }

        // Current page
        if (currentPage !== 1 && currentPage !== totalPages) {
          pageNumbers.push(currentPage);
        }

        // Pages after current
        if (currentPage < totalPages - 1) {
          pageNumbers.push(currentPage + 1);
        }

        if (currentPage < totalPages - 2) {
          pageNumbers.push("...");
        }

        pageNumbers.push(totalPages);
      } else {
        // Show all pages when there are few
        for (let i = 1; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      }

      return pageNumbers;
    };

    const handlePageChange = (page) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    };

    return (
      <View
        style={{
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          marginTop: 20,
          gap: 10,
        }}
      >
        <TouchableOpacity
          style={navBtnStyle(false, currentPage === 1)}
          onPress={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk' }}>Previous</Text>
        </TouchableOpacity>

        {getPageNumbers().map((page, index) =>
          page === "..." ? (
            <Text key={`ellipsis-${index}`} style={{ color: colors.textSecondary, fontFamily: 'VastagoGrotesk' }}>
              ...
            </Text>
          ) : (
            <TouchableOpacity
              key={`page-${page}`}
              style={{
                width: 32,
                height: 32,
                borderRadius: 16,
                ...navBtnStyle(currentPage === page),
                justifyContent: 'center',
                alignItems: 'center',
                paddingHorizontal: 0,
                paddingVertical: 0,
              }}
              onPress={() => handlePageChange(page)}
            >
              <Text style={{ color: currentPage === page ? '#fff' : colors.textPrimary, fontFamily: 'VastagoGrotesk', fontSize: 13 }}>
                {page}
              </Text>
            </TouchableOpacity>
          )
        )}

        <TouchableOpacity
          style={navBtnStyle(false, currentPage === totalPages)}
          onPress={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <Text style={{ color: colors.textPrimary, fontFamily: 'VastagoGrotesk' }}>Next</Text>
        </TouchableOpacity>
      </View>
    );
  };

  const renderVenueFilters = () => {
    return (
      <View
        style={{
          width: "100%",
          maxWidth: 1800,
          marginBottom: 20,
          paddingHorizontal: 20,
        }}
      >
        {/* Search Input */}
        <TextInput
          nativeID={ALL_VENUES_SEARCH_ID}
          style={{
            backgroundColor: colors.inputBg,
            borderRadius: 8,
            padding: 15,
            color: colors.inputText,
            fontFamily: "VastagoGrotesk",
            fontSize: 16,
            marginBottom: 20,
            width: "100%",
            borderWidth: 1,
            borderColor: colors.inputBorder,
          }}
          placeholder="Search venues..."
          placeholderTextColor={searchPlaceholderColor}
          value={venueSearchQuery}
          onChangeText={setVenueSearchQuery}
        />

        {/* Filter Groups */}
        <View style={{ flexDirection: "row", alignItems: "flex-end", justifyContent: "space-between", flexWrap: 'wrap', gap: 16 }}>
          <View style={{ flex: 1, minWidth: 280 }}>
            <View style={{ marginBottom: 20 }}>
              <Text
                style={{
                  color: colors.textPrimary,
                  fontFamily: "VastagoGrotesk",
                  fontSize: 16,
                  fontWeight: '600',
                  marginBottom: 10,
                }}
              >
                Filter by:
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                {[
                  { value: "all", label: "Active Venues" },
                  { value: "with_photos", label: "With Photos" },
                  { value: "without_photos", label: "Need Photos" },
                  { value: "not_found", label: "Not Found" },
                  { value: "archived", label: "Archived", danger: true },
                ].map((filter) => (
                  <TouchableOpacity
                    key={filter.value}
                    style={pillStyle(venueFilter === filter.value, filter.danger)}
                    onPress={() => setVenueFilter(filter.value)}
                  >
                    <Text style={pillTextStyle(venueFilter === filter.value)}>
                      {filter.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Sort Options */}
            <View>
              <Text
                style={{
                  color: colors.textPrimary,
                  fontFamily: "VastagoGrotesk",
                  fontSize: 16,
                  fontWeight: '600',
                  marginBottom: 10,
                }}
              >
                Sort by:
              </Text>
              <View
                style={{
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: 10,
                }}
              >
                {[
                  { value: "name", label: "Name" },
                  { value: "upvotes", label: "Most Upvotes" },
                  { value: "downvotes", label: "Most Downvotes" },
                  { value: "ratio", label: "Best Ratio" },
                ].map((sort) => (
                  <TouchableOpacity
                    key={sort.value}
                    style={pillStyle(venueSortBy === sort.value)}
                    onPress={() => setVenueSortBy(sort.value)}
                  >
                    <Text style={pillTextStyle(venueSortBy === sort.value)}>
                      {sort.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </View>
          <View>
            <TouchableOpacity
              style={{
                backgroundColor: "#3D7FE6",
                paddingVertical: 15,
                paddingHorizontal: 20,
                borderRadius: 8,
                alignItems: "center",
                justifyContent: "center",
              }}
              onPress={handleExportCSV}
            >
              <Text style={{
                color: "#ffffff",
                fontFamily: "VastagoGrotesk",
                fontSize: 14,
                fontWeight: '600',
              }}>
                Export Venues to CSV
              </Text>
          </TouchableOpacity>
          </View>
        </View>

      </View>
    );
  };

  const renderVotes = (label, upVotes, downVotes, textColor, mutedColor) => {
    const totalVotes = upVotes + downVotes;
    const pct = totalVotes > 0 ? ((upVotes / totalVotes) * 100).toFixed(1) : '0.0';
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: 4,
          opacity: totalVotes > 0 ? 1 : 0.55,
        }}
      >
        <Text style={{ width: 44, color: mutedColor, fontSize: 11, fontWeight: '600', fontFamily: 'VastagoGrotesk' }}>
          {label}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, justifyContent: 'flex-end' }}>
          <Text style={{ color: '#2ecc71', fontSize: 11, fontFamily: 'VastagoGrotesk' }}>👍 {upVotes}</Text>
          <Text style={{ color: '#e74c3c', fontSize: 11, fontFamily: 'VastagoGrotesk' }}>👎 {downVotes}</Text>
          <Text style={{ color: textColor, fontSize: 11, fontWeight: '600', fontFamily: 'VastagoGrotesk', minWidth: 72, textAlign: 'right' }}>
            {pct}% pos
          </Text>
        </View>
      </View>
    );
  };

  const actionButtonStyle = {
    flexBasis: '48%',
    flexGrow: 1,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
  };

  const renderVenue = (venue) => {
    const backgroundImage = getVenueBackgroundImage(venue);
    const hasPhotoBg = Boolean(backgroundImage);
    const upVotes = venue.upVotes || 0;
    const downVotes = venue.downVotes || 0;
    const specsUpVotes = venue.specsUpVotes || 0;
    const specsDownVotes = venue.specsDownVotes || 0;
    const linksUpVotes = venue.linksUpVotes || 0;
    const linksDownVotes = venue.linksDownVotes || 0;
    const totalVotes = upVotes + downVotes;
    const ratio =
      totalVotes > 0 ? ((upVotes / totalVotes) * 100).toFixed(1) : "N/A";

    const onPhoto = hasPhotoBg;
    const titleColor = onPhoto ? '#ffffff' : colors.textPrimary;
    const subColor = onPhoto ? 'rgba(255,255,255,0.88)' : colors.textSecondary;
    const mutedColor = onPhoto ? 'rgba(255,255,255,0.7)' : colors.textSecondary;
    const panelBg = onPhoto ? 'rgba(0,0,0,0.5)' : colors.cardBgSubtle;
    const panelBorder = onPhoto ? 'transparent' : colors.cardBorder;
    const contentScrim = onPhoto ? 'rgba(0,0,0,0.62)' : colors.cardBg;

    return (
      <TouchableOpacity
        key={venue.id}
        style={[adminStyles.venueCard, { minHeight: 380 }]}
        onPress={() => handleVenueClick(venue)}
      >
        <ImageBackground
          source={backgroundImage ? { uri: backgroundImage } : undefined}
          style={[
            adminStyles.venueCardBackground,
            { width: "100%", minHeight: 380 },
          ]}
          imageStyle={adminStyles.venueCardBackgroundImage}
        >
          <View
            style={[
              adminStyles.venueCardContent,
              {
                flex: 1,
                minHeight: 380,
                backgroundColor: contentScrim,
                justifyContent: 'space-between',
                paddingTop: 16,
                paddingBottom: 14,
                paddingHorizontal: 14,
              },
            ]}
          >
            {/* Venue info */}
            <View>
              <Text
                style={[
                  adminStyles.venueName,
                  {
                    color: titleColor,
                    textAlign: 'left',
                    fontSize: 20,
                    marginBottom: 4,
                    textShadow: onPhoto ? '0 1px 3px rgba(0,0,0,0.6)' : 'none',
                  },
                ]}
                numberOfLines={2}
              >
                {venue.name}
              </Text>
              <Text
                style={[
                  adminStyles.venueLocation,
                  { color: subColor, textShadow: onPhoto ? '0 1px 2px rgba(0,0,0,0.5)' : 'none', marginBottom: 6 },
                ]}
                numberOfLines={1}
              >
                {[venue.city, venue.state].filter(Boolean).join(', ') || 'Location unknown'}
              </Text>
              <Text
                style={[
                  adminStyles.venuePhotoCount,
                  { color: mutedColor, marginBottom: 0, textShadow: 'none' },
                ]}
              >
                Photos: {venue.photos?.length || 0}
              </Text>
              {venue.notFoundOnLiveNation && (
                <Text
                  style={{
                    color: '#e74c3c',
                    fontSize: 11,
                    fontFamily: 'VastagoGrotesk',
                    marginTop: 6,
                    fontWeight: '600',
                  }}
                >
                  Not found on LiveNation
                  {venue.notFoundTimestamp &&
                    ` (${new Date(venue.notFoundTimestamp).toLocaleDateString()})`}
                </Text>
              )}
            </View>

            {/* Vote stats */}
            <View
              style={{
                backgroundColor: panelBg,
                borderRadius: 8,
                padding: 10,
                marginTop: 12,
                borderWidth: 1,
                borderColor: panelBorder,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingBottom: 8,
                  marginBottom: 6,
                  borderBottomWidth: 1,
                  borderBottomColor: onPhoto ? 'rgba(255,255,255,0.15)' : colors.divider,
                }}
              >
                <Text style={{ color: mutedColor, fontSize: 11, fontWeight: '700', fontFamily: 'VastagoGrotesk', letterSpacing: 0.5 }}>
                  OVERALL
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                  <Text style={{ color: '#2ecc71', fontSize: 12, fontFamily: 'VastagoGrotesk' }}>👍 {upVotes}</Text>
                  <Text style={{ color: '#e74c3c', fontSize: 12, fontFamily: 'VastagoGrotesk' }}>👎 {downVotes}</Text>
                  <Text style={{ color: titleColor, fontSize: 12, fontWeight: '700', fontFamily: 'VastagoGrotesk' }}>
                    {ratio}% Positive
                  </Text>
                </View>
              </View>
              {renderVotes('Specs', specsUpVotes, specsDownVotes, titleColor, mutedColor)}
              {renderVotes('Links', linksUpVotes, linksDownVotes, titleColor, mutedColor)}
            </View>

            {/* Actions — same order, 2×2 grid */}
            {venue.archived ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                <TouchableOpacity
                  style={[actionButtonStyle, { backgroundColor: '#DAA520', width: '100%', flexBasis: '100%' }]}
                  onPress={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleRestoreVenue(venue);
                  }}
                >
                  <Text style={adminStyles.uploadButtonText}>Restore Venue</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                <TouchableOpacity
                  style={[actionButtonStyle, { backgroundColor: 'rgba(46, 204, 113, 0.92)' }]}
                  onPress={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handlePhotoUpload(venue.id, file, "background");
                      }
                    };
                    input.click();
                  }}
                >
                  <Text style={[adminStyles.uploadButtonText, { fontSize: 12, textAlign: 'center' }]}>
                    {venue.backgroundImage ? "Update Background" : "Add Background"}
                  </Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[actionButtonStyle, { backgroundColor: 'rgba(231, 76, 60, 0.92)' }]}
                  onPress={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeletePhotos(venue.id);
                  }}
                >
                  <Text style={[adminStyles.uploadButtonText, { fontSize: 12, textAlign: 'center' }]}>Delete Photos</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[actionButtonStyle, { backgroundColor: '#E49B0F' }]}
                  onPress={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleArchiveVenue(venue);
                  }}
                >
                  <Text style={[adminStyles.uploadButtonText, { fontSize: 12, textAlign: 'center' }]}>Archive Venue</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[actionButtonStyle, { backgroundColor: 'rgba(192, 57, 43, 0.95)' }]}
                  onPress={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteVenue(venue);
                  }}
                >
                  <Text style={[adminStyles.uploadButtonText, { fontSize: 12, textAlign: 'center' }]}>Delete Venue</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        </ImageBackground>
      </TouchableOpacity>
    );
  };

  const filteredVenues = getFilteredVenues();
  const totalVenues = venues.filter((venue) => {
    const matchesSearch =
      venue.name.toLowerCase().includes(venueSearchQuery.toLowerCase()) ||
      (venue.city &&
        venue.city.toLowerCase().includes(venueSearchQuery.toLowerCase())) ||
      (venue.state &&
        venue.state.toLowerCase().includes(venueSearchQuery.toLowerCase()));

    const hasPhotos =
      venue.backgroundImage ||
      (venue.photos && venue.photos.length > 0) ||
      (venue.venue_media &&
        typeof venue.venue_media === "string" &&
        JSON.parse(venue.venue_media).photos?.length > 0);

    const matchesFilter =
      venueFilter === "all" ||
      (venueFilter === "with_photos" && hasPhotos) ||
      (venueFilter === "without_photos" && !hasPhotos) ||
      (venueFilter === "not_found" && venue.notFoundOnLiveNation);

    return matchesSearch && matchesFilter;
  }).length;

  if (selectedVenue) {
    return (
      <View style={{ width: "100%", maxWidth: 1400, padding: 20 }}>
        <TouchableOpacity
          style={{
            flexDirection: "row",
            alignItems: "center",
            padding: 15,
            marginBottom: 20,
            backgroundColor: colors.cardBgSubtle,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: colors.cardBorder,
            alignSelf: "flex-start",
          }}
          onPress={() => setSelectedVenue(null)}
        >
          <Text style={{ color: colors.textPrimary, fontSize: 16, fontWeight: "500", fontFamily: 'VastagoGrotesk' }}>
            ← Back to Venues
          </Text>
        </TouchableOpacity>

        <VenueCard
          venue={selectedVenue}
          editingVenue={editingVenue}
          editFormData={editFormData}
          onStartEditing={startEditing}
          onCancelEdit={handleCancelEdit}
          onSaveVenue={handleSaveVenue}
          onEditField={handleEditField}
          showStatus={false}
          showVotes={true}
        />
      </View>
    );
  }

  return (
    <View style={{ flex: 1, width: "100%" }}>
      {/* Search and filter controls */}

      {renderVenueFilters()}

      {isLoading ? (
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', marginTop: 20 }}>
          <ActivityIndicator size="large" color={colors.statAccent} />
          <Text style={{ marginTop: 10, color: colors.textSecondary, fontSize: 16, fontFamily: 'VastagoGrotesk' }}>Loading...</Text>
        </View>
      ) : (
        <>
          <View style={adminStyles.venueGrid} className="venueGrid">
            {filteredVenues.map((venue) => renderVenue(venue))}
          </View>

          {filteredVenues.length < totalVenues && (
            <TouchableOpacity
              style={adminStyles.loadMoreButton}
              onPress={handleLoadMore}
            >
              <Text style={adminStyles.loadMoreButtonText}>
                Load More ({filteredVenues.length} of {totalVenues})
              </Text>
            </TouchableOpacity>
          )}

          {renderPaginationControls(totalVenues)}
        </>
      )}
    </View>
  );
};

export default VenuesList;
